import { Component } from 'preact';
import { Maybe, RosterEntry } from '../../common/data.js';
import { apply, capitalize, ensure, findMatch, generateId, maybe, num, within } from '../../common/util.js';
import { showAlert, showConfirm, showPrompt } from './popup.js';
import { Icon } from './icons.js';
import { Input } from './input.js';
import { getSavedRosters, getSizeChart, saveRoster } from '../lib/api.js';
import {
	getRosterSizeSort,
	getSizeGroup,
	getSizeName,
	PublicProduct,
	sizeGroups,
	WithFeatures,
} from '../../common/data/products.js';
import { CountryPicker } from './country.js';
import { initRosterEntry } from '../../common/data/roster.js';
import { DesignRosterFields, RosterPartType } from '../../common/data/designs.js';
import { getEventValue, submitQueue } from '../lib/util.js';
import { getContentUrlPrefix } from '../../common/data/images.js';

export interface RosterEditorProps {
	item?: string;
	roster: RosterEntry[];
	product: PublicProduct & WithFeatures;
	onChange: (id: string, data: Partial<RosterEntry>) => void;
	onDelete: (id: string) => void;
	onPreview?: (rid: Maybe<string>) => void;
	onFocus?: (id: string) => void;
	onDone?: () => void;
	previewing?: Maybe<string>;
	fields: DesignRosterFields;
	replace: (roster: RosterEntry[]) => void;
	showSizeCharts?: () => void;
	hideRosterEditorButtons?: boolean;
	rosterButtonHandler?: () => void;
	sizesInStock?: Maybe<string[]>;
	sizesOfferedByVendor?: Maybe<string[]>;
	
	/** @deprecated Seems unused at the moment. */
	showPart?: Maybe<(type: RosterPartType) => void>;
}

export interface RosterEditorState {
	confirm?: boolean;
	id: number;
	numberOfNewRows?: Maybe<number>;
	saving?: boolean;
}

interface SizeOption {
	size: string;
	name: string;
	group: string;
	inStock: boolean;
	sort: number;
}

abstract class RosterEditorBase extends Component<RosterEditorProps, RosterEditorState> {
	
	sizeGroups: string[];
	sizes: SizeOption[];

	constructor(props: RosterEditorProps) {
		super(props);
		const sizes = ensure(this.props.sizesOfferedByVendor || this.props.product.features?.sizes);
		this.sizes = sizes.map(size => ({
			size,
			sort: getRosterSizeSort(size),
			group: sizeGroups[getSizeGroup(size)]!,
			name: getSizeName(size),
			inStock: this.props.sizesInStock ? this.props.sizesInStock.includes(size) : true,
		}));
		this.sizeGroups = [...new Set(this.sizes.map(s => s.group))];
		
	}
	
	addRows = async () => {
		const count = this.state.numberOfNewRows || 1;
		for (let i = 0; i < count; i++) this.props.onChange(generateId(), { quantity: 1 });
	};
	
	removeRow = (id: string) => async () => {
		const confirm = await showConfirm('Are you sure?', ['Yes', 'No'], { width: 250 });
		if (!confirm) return;
		this.props.onDelete(id);
	};
	
	removeAll = async () => {
		if (!await showConfirm('Are you sure?', ['Clear', 'Cancel'])) return;
		this.props.replace([initRosterEntry()]);
	};
	
	@submitQueue
	async save() {
		const res = await showPrompt(<div>
			<span>Name your roster</span><br />
		</div>);
		if (typeof res !== 'string') return;
		
		const roster = this.props.roster;
		const { rosterId } = await saveRoster({
			roster,
			name: res,
		});
		
		const needToOpen = await showConfirm(
			`The roster ${res} was saved! Do you want to open it in dashboard?`,
			['Yes', 'No'],
		);
		if (needToOpen) location.href = `/dashboard/rosters/edit/${rosterId}`;
	}
	
	@submitQueue
	async load() {
		
		const { rosters } = await getSavedRosters();
		
		let selected: Maybe<number> = null;
		const res = await showConfirm(<div class="roster-editor__upload-wrapper">
			<div class="roster-editor__upload-title">Choose from your saved rosters</div>
			<select class="roster-editor__upload" onChange={(e) => { selected = Number(getEventValue(e)); }}>
				<option disabled={true} selected={true}>Select Roster</option>
				{rosters.map(rost => <option value={rost.id} selected={selected == rost.id}>{rost.name}</option>)}
			</select>
		</div>, ['Apply', 'Cancel'], { width: 400 });
		if (!res || !selected) return;
		
		const { size_chart } = await getSizeChart({ product: this.props.product.id });
		const chartEntries = size_chart ? Object.keys(size_chart.values) : undefined;
		const chartGenders = chartEntries?.map(k => k.split('-')[0]);
		
		// Wipe sizes that do not exist in the product. Should show an alert.
		let sizesWiped = false;
		let sizesMismatchChart = false;
		let fieldsMismatch = false;
		const sizes = this.props.sizesOfferedByVendor ?? this.props.product.features!.sizes;
		const entries = ensure(findMatch(rosters, { id: selected })?.roster).map(re => {
			const size = re.size && sizes.includes(re.size) ? re.size : null;
			if (!size) sizesWiped = true;
			if (!sizesMismatchChart && chartGenders && re.size && !chartGenders.includes(re.size.split('-')[0])) sizesMismatchChart = true;
			
			if (
				(re.flag && this.props.fields.flag !== 'present')
				|| (re.name && this.props.fields.name !== 'present')
				|| (re.alias && this.props.fields.alias !== 'present')
				|| (re.number && this.props.fields.number !== 'present')
			) fieldsMismatch = true;
			
			return {
				...re,
				size,
			};
		});
		
		this.props.replace(entries);
		
		if (sizesMismatchChart) await showAlert(
			'You\'ve tried adding a roster to a product that utilizes a different size chart so no sizes can be imported.',
			'error',
		);
		else if (sizesWiped) await showAlert(
			'This roster contains sizes that aren\'t available with the selected product. The unavailable sizes will be need to be re-selected.',
			'error',
		);
		
		if (fieldsMismatch) await showAlert(
			'You\'ve uploaded personalization fields (NAME, NUMBER, ALIAS, FLAG) that either aren\'t added to your product mock up or aren\'t able to be added to this product. Please double check your product design mockup. Once you add the graphic component to your design your roster entries will appear.',
			'error',
		);
		
	}
	
}

export class RosterEditor extends RosterEditorBase {

	render(props: RosterEditorProps, state: RosterEditorState) {
		
		const { sizes } = this;
		const {
			roster,
			fields,
			product,
			onChange,
			showSizeCharts,
			hideRosterEditorButtons,
			onPreview,
			previewing,
		} = props;
		const { numberOfNewRows } = state;
		
		const updater = (id: string, key: keyof RosterEntry) => async (value?: string|number) => onChange(id, { [key]: value });
		
		const haveSizeChart = product.size_chart && showSizeCharts;
		
		const th = 'roster-editor-table__heading';
		
		return (<div class="roster-editor">
			{ !hideRosterEditorButtons && <div class="roster-editor__header-row">
				<button onClick={this.load.bind(this)}>Load Roster</button>
				<button onClick={this.save.bind(this)}>Save Roster</button>
			</div> }
			<table class="roster-editor-table">
				
				<thead class="roster-editor-table__head">
					<tr class="roster-editor-table__tablerow">
						{onPreview && <th
							class="roster-editor-table__preview-icon"
							title="Preview placeholders"
							onClick={() => onPreview && onPreview(null)}
						><Icon name={!previewing ? 'eye-filled': 'eye'} /></th>}
						{fields.name === 'present' && <th class={th}>Name</th>}
						{fields.alias === 'present' && <th class={th}>Alias</th>}
						{fields.number === 'present' && <th class={th}>Number</th>}
						{fields.flag === 'present' && <th class={th}>Flag</th>}
						{sizes.length > 1 && <th class={th}>
							Size
							{haveSizeChart && <span>{' ('}<a href="#" onClick={() => showSizeCharts()}>view chart</a>)</span>}
						</th>}
						<th class={th}>Quantity</th>
						<th class={th} />
					</tr>
				</thead>
				
				<tbody class="roster-editor-table__body">
					{roster.map(row => <tr class="roster-editor-table__tablerow" key={row.id}>
						{onPreview && <td
							class="roster-editor-table__cell roster-editor-table__preview-icon"
							onClick={() => onPreview(row.id)}
							title="Preview entry"
						><Icon name={previewing == row.id ? 'eye-filled' : 'eye'} /></td>}
						{(['name', 'alias', 'number'] as const).filter(key => fields[key]).map(key => fields[key] == 'present' && <td class={`roster-editor-table__cell roster-editor-table__${key}`}>
							<Input
								class="roster-editor-table__input"
								value={row[key as keyof RosterEntry]}
								handler={updater(row.id, key)}
								placeholder="-"
								title={capitalize(key)}
							/>
						</td>)}
						{fields.flag === 'present' && <td class="roster-editor-table__cell roster-editor-table__flag">
							<div class="roster-editor-table__flag-selector-wrapper">
								<CountryPicker
									flag={true}
									value={row.flag}
									handler={country => updater(row.id, 'flag')(country?.id)}
									title="Flag"
								/>
								<img src={`${getContentUrlPrefix()}/data/flags/${row.flag || 'unknown'}.png`} alt="flag" />
								<Icon name="triangle-down" />
							</div>
						</td>}
						{sizes.length > 1 && <td class="roster-editor-table__cell roster-editor-table__size">
							<select onChange={(e) => onChange(row.id, { size: getEventValue(e) })}>
								<option disabled selected={row.size === null || row.size === undefined} value={undefined}>Select size</option>
								{sizes.map(({ size, name, inStock }) => <option key={size} value={size} selected={row.size === size} data-oos={!inStock}>{name} {!inStock && '(out of stock)'}</option>)}
							</select>
							{row.size?.replace(/^[AW]-/, '') || '-'}
							<Icon name="triangle-down" />
						</td>}
						<td class="roster-editor-table__cell roster-editor-table__quantity">
							<div class="roster-editor-table__quantity-inner">
								<button
									class="roster-editor__adjust"
									onClick={() => onChange(
										row.id,
										{ quantity: within(1, (maybe(row.quantity, Number) ?? 1) - 1, 9999) },
									)}
									title="Decrease quantity"
								>-</button>
								<Input
									class="roster-editor-table__input"
									value={row.quantity || ''}
									handler={value => apply(Number(value), q => {
										if (isFinite(q)) onChange(row.id, { quantity: within(1, q, 9999) });
									})}
									placeholder="-"
									title="Quantity"
								/>
								<button
									class="roster-editor__adjust"
									onClick={() => onChange(
										row.id,
										{ quantity: within(1, (maybe(row.quantity, Number) ?? 1) + 1, 9999) },
									)}
									title="Increase quantity"
								>+</button>
							</div>
						</td>
						<td class="roster-editor-table__cell roster-editor-table__cell--actions">
							<button
								title="Remove entry"
								onClick={this.removeRow(row.id)}
							><Icon name="close" /></button>
						</td>
					</tr>)}
				</tbody>
			</table>
			<div class="roster-editor__row">
				<button class="roster-editor__row-btn roster-editor__row-btn-clear" onClick={this.removeAll}>Clear roster</button>
				<Input class="roster-editor__row-input-num-rows" attributes={{ id: 'rows', placeholder: 'New rows' }} value={numberOfNewRows} handler={value => this.setState({ numberOfNewRows: apply(Number(value), n => isFinite(n) ? within(1, n, 20) : null) })} />
				<button class="roster-editor__row-btn roster-editor__row-btn-add-rows" title="Add rows" onClick={this.addRows}>+</button>
			</div>
		</div>
		);
	}

}

export class SizeOnlyRosterEditor extends RosterEditorBase {
	
	render(props: RosterEditorProps, state: RosterEditorState) {
		
		const { sizes, sizeGroups } = this;
		const {
			item,
			roster,
			product,
			showSizeCharts,
		} = props;
		
		const haveSizeChart = product.size_chart && showSizeCharts;
		
		return <div class="roster-editor">
		
			<div class="roster-editor-sizes">
				{sizeGroups.map(g => <div className="roster-editor-sizes__group">
					
					{sizeGroups.length > 1 && <div>{g}:</div>}
					
					<div className="roster-editor-sizes__group-sizes">
						{sizes.filter(s => s.group == g).map(s => {
							const re = roster.find(re => re.size == s.size);
							return <div className="roster-editor-sizes__size">
								<label htmlFor={`${item}-${s.size}`}>{getSizeName(s.size, false)}:</label>
								<div className="roster-editor-sizes__entry">
									<Input
										class="roster-editor-sizes__input"
										value={re?.quantity || ''}
										handler={value => apply(Number(value), q => {
											if (isFinite(q)) this.setSizeQuantity(s.size, q);
										})}
										id={`${item}-${s.size}`}
										onFocus={e => e.currentTarget.select()}
									/>
								</div>
							</div>;
						})}
					</div>
					
				</div>)}
			</div>
			
			{haveSizeChart && <a href="#" onClick={() => showSizeCharts()}>View size chart</a>}
		
		</div>;
		
	}
	
	setSizeQuantity(size: string, quantity: number) {
		quantity = within(0, quantity, 9999);
		const re = this.props.roster.find(re => re.size == size);
		if (quantity) this.props.onChange(re?.id ?? generateId(), { size, quantity });
		else if (re) this.props.onDelete(re.id);
	}
	
	componentDidMount() {
		const { props, sizes } = this;
		const { roster } = props;
		const seen: Record<string, RosterEntry> = {};
		for (const re of roster) {
			if (!re.size || !re.quantity || !sizes.some(s => s.size == re.size)) {
				this.props.onDelete(re.id);
				continue;
			}
			const prev = seen[re.size];
			if (prev) {
				prev.quantity = num(prev.quantity!) + num(re.quantity);
				this.props.onChange(prev.id, { quantity: prev.quantity });
				this.props.onDelete(re.id);
				continue;
			}
			seen[re.size] = re;
		}
	}
	
}
